'use client';

import { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Video, Smile, Send, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { createClient } from '@/lib/supabase/browser';
import {
  storageUploadWithProgress,
  describeUploadError,
  MAX_REEL_SIZE_BYTES,
} from '@/lib/upload/storageUploadXhr';
import EmojiPickerPopover from '@/components/dm/EmojiPickerPopover';

export type GroupComposerSendPayload = {
  content: string;
  messageType: 'text' | 'image' | 'video';
  mediaUrl: string | null;
  mediaThumbnail: string | null;
};

const MAX_LEN = 1000;
const COUNTER_THRESHOLD = 800;

type Pending = {
  kind: 'image' | 'video';
  file: File;
  storagePath: string;
  publicUrl: string;
  previewUrl: string;
};

export default function GroupComposer({
  groupId,
  channelName,
  userId,
  onSend,
  disabled,
}: {
  groupId: string;
  channelName: string;
  userId: string;
  onSend: (payload: GroupComposerSendPayload) => Promise<void>;
  disabled?: boolean;
}) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const emojiBtnRef = useRef<HTMLButtonElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    const next = Math.min(160, Math.max(40, el.scrollHeight));
    el.style.height = `${next}px`;
  }, [text]);

  const canSend = !disabled && !sending && !uploading && (text.trim().length > 0 || !!pending);

  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    if (!el) {
      setText((t) => (t + emoji).slice(0, MAX_LEN));
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = (text.slice(0, start) + emoji + text.slice(end)).slice(0, MAX_LEN);
    setText(next);
    requestAnimationFrame(() => {
      const pos = start + emoji.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const startUpload = async (file: File) => {
    setError(null);
    const t = (file.type || '').toLowerCase();
    const kind: 'image' | 'video' | null = t.startsWith('image/')
      ? 'image'
      : t.startsWith('video/')
      ? 'video'
      : null;
    if (!kind) {
      setError('Unsupported file type. Pick an image or video.');
      return;
    }
    if (file.size === 0) {
      setError(
        "Couldn't read the file. If you picked it from cloud storage, download it to your device first.",
      );
      return;
    }
    if (file.size > MAX_REEL_SIZE_BYTES) {
      setError('File is too large. Maximum size is 100 MB.');
      return;
    }

    const supabase = createClient();
    const sanitize = (name: string) =>
      name
        .normalize('NFKD')
        .replace(/[^\w.\-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^[._-]+|[._-]+$/g, '');
    const safeName = sanitize(file.name) || `${kind}.bin`;
    const uuid =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const path = `groups/${groupId}/${userId}/${uuid}-${safeName}`;
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    setUploadPct(0);

    let previewUrl: string | null = null;
    try {
      previewUrl = URL.createObjectURL(file);
      await storageUploadWithProgress({
        supabase,
        bucket: 'user-assets',
        path,
        file,
        contentType: file.type || undefined,
        cacheControl: '3600',
        upsert: false,
        signal: controller.signal,
        onProgress: ({ pct }) => setUploadPct(pct),
      });
      const {
        data: { publicUrl },
      } = supabase.storage.from('user-assets').getPublicUrl(path);
      setPending({ kind, file, storagePath: path, publicUrl, previewUrl });
    } catch (err) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setError(describeUploadError(err));
    } finally {
      abortRef.current = null;
      setUploading(false);
      setUploadPct(0);
    }
  };

  const cancelUpload = () => abortRef.current?.abort();

  const removePending = async () => {
    if (!pending) return;
    if (pending.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    try {
      const supabase = createClient();
      await supabase.storage.from('user-assets').remove([pending.storagePath]);
    } catch {
      // best-effort
    }
    setPending(null);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const messageType: 'text' | 'image' | 'video' = pending ? pending.kind : 'text';
      const mediaUrl = pending?.publicUrl ?? null;
      const mediaThumbnail = pending?.kind === 'image' ? pending.publicUrl : null;
      await onSend({
        content: text.trim(),
        messageType,
        mediaUrl,
        mediaThumbnail,
      });
      setText('');
      if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
      setPending(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="border-t border-border-subtle bg-surface-1 px-3 sm:px-4 pt-3"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 10px)' }}
    >
      {error && (
        <div
          role="alert"
          className="mb-2 px-3 py-2 rounded-sm text-xs bg-status-busy/10 border border-status-busy/30 text-status-busy flex items-start justify-between gap-2"
        >
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
            className="text-text-tertiary hover:text-text-primary"
          >
            <X className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        </div>
      )}

      {pending && (
        <div className="mb-2 flex items-start gap-2 p-2 rounded-md bg-surface-2 border border-border-subtle">
          {pending.kind === 'image' ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={pending.previewUrl}
              alt={pending.file.name}
              className="w-16 h-16 object-cover rounded-sm"
            />
          ) : (
            <div className="w-16 h-16 rounded-sm bg-surface-3 flex items-center justify-center">
              <Video className="w-6 h-6 text-text-secondary" strokeWidth={1.5} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs text-text-primary truncate">{pending.file.name}</p>
            <p className="text-[11px] text-text-tertiary mt-0.5">
              {(pending.file.size / (1024 * 1024)).toFixed(1)} MB · {pending.kind}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void removePending()}
            aria-label="Remove attachment"
            className="p-1 rounded-sm text-text-tertiary hover:text-text-primary hover:bg-surface-3"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      )}

      {uploading && (
        <div className="mb-2 px-1">
          <div className="flex items-center justify-between text-xs text-text-secondary mb-1">
            <span>Uploading… {uploadPct}%</span>
            <button
              type="button"
              onClick={cancelUpload}
              className="text-status-busy hover:underline"
            >
              Cancel
            </button>
          </div>
          <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${uploadPct}%` }}
            />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="rounded-md bg-surface-2 border border-border-default focus-within:border-accent transition-colors">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleSubmit();
              }
            }}
            placeholder={`Message #${channelName}…`}
            disabled={disabled || sending}
            rows={1}
            className="w-full bg-transparent text-sm text-text-primary placeholder-text-tertiary resize-none border-0 outline-none px-3.5 pt-3 pb-1"
            style={{ minHeight: 40, maxHeight: 160 }}
            aria-label={`Message #${channelName}`}
          />
          <div className="flex items-center gap-1 px-1.5 pb-1.5">
            <ToolbarBtn
              label="Add photo"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploading || sending || !!pending}
            >
              <ImageIcon className="w-4 h-4" strokeWidth={1.75} />
            </ToolbarBtn>
            <ToolbarBtn
              label="Add video"
              onClick={() => videoInputRef.current?.click()}
              disabled={uploading || sending || !!pending}
            >
              <Video className="w-4 h-4" strokeWidth={1.75} />
            </ToolbarBtn>
            <ToolbarBtn
              ref={emojiBtnRef}
              label="Emoji"
              onClick={() => setEmojiOpen((v) => !v)}
              active={emojiOpen}
            >
              <Smile className="w-4 h-4" strokeWidth={1.75} />
            </ToolbarBtn>

            <div className="ml-auto flex items-center gap-2">
              {text.length >= COUNTER_THRESHOLD && (
                <span
                  className={cn(
                    'text-[11px] font-mono',
                    text.length >= MAX_LEN ? 'text-status-busy' : 'text-text-tertiary',
                  )}
                >
                  {text.length}/{MAX_LEN}
                </span>
              )}
              <button
                type="submit"
                disabled={!canSend}
                className={cn(
                  'h-[30px] px-3.5 rounded-sm inline-flex items-center gap-1.5 text-sm font-medium transition-colors',
                  canSend
                    ? 'bg-accent text-text-on-accent hover:bg-accent-hover'
                    : 'bg-surface-3 text-text-tertiary border border-border-subtle cursor-not-allowed',
                )}
              >
                <Send className="w-3.5 h-3.5" strokeWidth={1.75} />
                Send
              </button>
            </div>
          </div>
        </div>

        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void startUpload(f);
          }}
        />
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void startUpload(f);
          }}
        />
      </form>

      <p className="mt-1.5 text-[11px] text-text-tertiary">
        Use <kbd className="font-mono">Shift+Enter</kbd> for new line.
      </p>

      {emojiOpen && (
        <EmojiPickerPopover
          anchorRef={emojiBtnRef as React.RefObject<HTMLElement>}
          onSelect={insertEmoji}
          onClose={() => setEmojiOpen(false)}
        />
      )}
    </div>
  );
}

import { forwardRef } from 'react';

type ToolbarBtnProps = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  children: React.ReactNode;
};

const ToolbarBtn = forwardRef<HTMLButtonElement, ToolbarBtnProps>(function ToolbarBtn(
  { label, onClick, disabled, active, children },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      disabled={disabled}
      className={cn(
        'w-8 h-8 rounded-sm inline-flex items-center justify-center transition-colors',
        active
          ? 'bg-accent-soft text-accent'
          : 'text-text-secondary hover:text-text-primary hover:bg-surface-3',
        disabled && 'opacity-40 cursor-not-allowed',
      )}
    >
      {children}
    </button>
  );
});
